import { Head, router, usePage } from "@inertiajs/react"
import { useState } from "react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import type { PageProps } from "@/types/inertia"

type Props = PageProps & {
  ttl_hours: number
  expired: boolean
}

const TITLE = "Online sessions"
const DESCRIPTION = "Create a private video-session room and get the links to send to a client."

export default function AdminVideoSessionsNew() {
  const { ttl_hours, expired } = usePage<Props>().props
  const [creating, setCreating] = useState(false)

  function create() {
    setCreating(true)
    router.post("/admin/video-sessions", {}, { onFinish: () => setCreating(false) })
  }

  return (
    <AdminShell>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <PageHeader
        title={TITLE}
        description="Self-hosted, encrypted 1:1 video sessions in the browser — nothing to install, nothing recorded."
        actions={
          <Button onClick={create} disabled={creating}>
            {creating ? "Creating…" : "Create session room"}
          </Button>
        }
      />

      {expired && (
        <div role="alert" className="callout callout-signal mt-6">
          That session link had expired, so its links can't be shown. Create a new room below.
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <div className="callout">
          <h2>How it works</h2>
          <ol className="mt-3 space-y-2">
            <li>Create a room. You get a <strong>host link</strong> (for you) and a <strong>client link</strong>.</li>
            <li>Send the client link to your client — by email or however you normally confirm appointments.</li>
            <li>At session time, open your host link. Your client waits in a waiting room until you admit them.</li>
            <li>End the session from the call screen; the room closes and the links stop working.</li>
          </ol>
          <p className="mt-4 text-ink-muted">
            Rooms expire automatically after {ttl_hours} hours, so create the room on the day of the
            session. One client per room — create a new room for each appointment.
          </p>
        </div>

        <div className="callout">
          <h2>Privacy</h2>
          <ul className="mt-3 space-y-2">
            <li>Video and audio travel directly between the two browsers, encrypted end to end.</li>
            <li>Nothing is recorded or transcribed — the capability does not exist.</li>
            <li>Links contain a random room code only. Never put a client's name alongside a link in an email subject or calendar entry.</li>
            <li>The host link is yours alone: anyone with it can host the session.</li>
          </ul>
        </div>
      </div>
    </AdminShell>
  )
}
